import React, { useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableFieldItemProps {
  id: string;
  children: (dragHandleProps: {
    ref: (element: HTMLElement | null) => void;
    [key: string]: any;
  }) => React.ReactNode;
}

/**
 * Simple wrapper component for sortable fields
 * Uses render prop pattern to pass drag handle props
 * Only the element with dragHandleProps will be draggable
 */
export const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  id,
  children
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    setActivatorNodeRef
  } = useSortable({ id });

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    }),
    [transform, transition, isDragging]
  );

  // Create drag handle props object
  const dragHandleProps = useMemo(
    () => ({
      ref: setActivatorNodeRef,
      ...attributes,
      ...listeners,
    }),
    [setActivatorNodeRef, attributes, listeners]
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'z-50' : ''}
    >
      {children(dragHandleProps)}
    </div>
  );
};
